import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import VendorTransactionModal from '../components/VendorTransactionModal';
import TransactionList from '../components/TransactionList';
import DateRangeDisplay from '../components/DateRangeDisplay';
import { vendorAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiPlus, FiPhone, FiFileText, FiCalendar, FiTrendingUp, FiTrendingDown, FiFilter } from 'react-icons/fi';
import { startOfMonth, endOfMonth, subMonths, isWithinInterval, parseISO } from 'date-fns';
import { formatAmount } from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './Pages.css';

const VendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, toneEnabled } = useTone();

  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showTxnModal, setShowTxnModal] = useState(false);

  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'current_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'last_3_months':
        return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
      case 'all':
      default:
        return { start: null, end: null };
    }
  }, [dateFilter]);

  // Filter transactions client-side based on date range
  const filteredTransactions = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return transactions;
    return transactions.filter((txn) => {
      try {
        const txnDate = parseISO(txn.transaction_date);
        return isWithinInterval(txnDate, { start: dateRange.start, end: dateRange.end });
      } catch {
        return true;
      }
    });
  }, [transactions, dateRange]);

  useEffect(() => {
    fetchVendorData();
  }, [id]);

  const fetchVendorData = async () => {
    setLoading(true);
    try {
      const response = await vendorAPI.getTransactions(id);
      const data = response.data.data;
      setVendor(data.vendor);
      setTransactions(data.transactions || []);
      setSummary(data.summary);
    } catch (error) {
      toast.error('Failed to load vendor details');
      navigate('/vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleTxnSuccess = () => {
    fetchVendorData();
  };

  /**
   * ENTERPRISE RULE: Internal transfers display as bank-like entries
   * For Vendor details, also shows direction-aware internal_transfer display
   */
  const getTransactionTypeInfo = (type, txn = null) => {
    // Handle internal_transfer with direction awareness
    if (type === 'internal_transfer' && txn) {
      const desc = (txn.description || '').toLowerCase();
      if (desc.startsWith('transfer to')) {
        return { label: toneEnabled ? 'Transfer bheja' : 'Transfer Out', icon: '−', color: '#E74C3C', class: 'expense' };
      }
      if (desc.startsWith('transfer from')) {
        return { label: toneEnabled ? 'Transfer aaya' : 'Transfer In', icon: '+', color: '#27AE60', class: 'income' };
      }
    }

    const types = {
      income: { label: toneEnabled ? 'Payment aaya' : 'Income', icon: '+', color: '#27AE60', class: 'income' },
      income_received: { label: toneEnabled ? 'Payment aaya' : 'Income', icon: '+', color: '#27AE60', class: 'income' },
      expense: { label: toneEnabled ? 'Kharcha kiya' : 'Expense', icon: '−', color: '#E74C3C', class: 'expense' },
      expense_paid: { label: toneEnabled ? 'Kharcha kiya' : 'Expense', icon: '−', color: '#E74C3C', class: 'expense' },
      internal_transfer: { label: toneEnabled ? 'Transfer' : 'Transfer', icon: '↔', color: '#7F8C8D', class: 'transfer' },
      payable: { label: toneEnabled ? 'Dena hai' : 'Payable', icon: '◯', color: '#F39C12', class: 'neutral' },
      receivable: { label: toneEnabled ? 'Lena hai' : 'Receivable', icon: '◎', color: '#3498DB', class: 'neutral' },
    };
    return types[type] || { label: type, icon: '•', color: '#7F8C8D', class: 'neutral' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message={t('common.loading')} />
      </Layout>
    );
  }

  if (!vendor) {
    return (
      <Layout>
        <div className="page-container">
          <p>{toneEnabled ? 'Dukandaar nahi mila' : 'Vendor not found'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        {/* Header */}
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/vendors')} title={toneEnabled ? 'Wapas' : 'Back'}>
              <FiArrowLeft size={20} />
            </button>
            <h1 style={{ margin: 0 }}>{vendor.name}</h1>
          </div>
          <button className="btn btn-primary" onClick={() => setShowTxnModal(true)}>
            <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {toneEnabled ? 'Lenden Jodo' : 'Add Transaction'}
          </button>
        </div>

        {/* Vendor Info Card */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            {vendor.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#7F8C8D' }}>
                <FiPhone size={16} />
                <span>{vendor.phone}</span>
              </div>
            )}
            {vendor.notes && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#7F8C8D' }}>
                <FiFileText size={16} />
                <span>{vendor.notes}</span>
              </div>
            )}
          </div>
        </div>

        {/* VENDOR ACCOUNTING: Only 3 Summary Cards (Source of Truth - docs/accounting/vendor-accounting.md) */}
        {summary && (
          <div className="summary-cards" style={{ marginBottom: 24 }}>
            {/* Card 1: Payable (Pending) */}
            <div className="summary-card" style={{ borderLeft: '4px solid #F39C12' }}>
              <h3>{toneEnabled ? 'Dena Baaki' : 'Payable (Pending)'}</h3>
              <p className="amount" style={{ color: '#F39C12' }}>
                {formatAmount(summary.pending_payable ?? Math.max((summary.total_payables || 0) - (summary.total_paid || 0), 0))}
              </p>
              <small style={{ color: '#7F8C8D', fontSize: '0.75rem' }}>
                {toneEnabled ? `Ab tak diya: ${formatAmount(summary.total_paid || summary.total_expense || 0)}` : `Paid so far: ${formatAmount(summary.total_paid || summary.total_expense || 0)}`}
              </small>
            </div>
            {/* Card 2: Receivable (Pending) */}
            <div className="summary-card" style={{ borderLeft: '4px solid #3498DB' }}>
              <h3>{toneEnabled ? 'Lena Baaki' : 'Receivable (Pending)'}</h3>
              <p className="amount" style={{ color: '#3498DB' }}>
                {formatAmount(summary.pending_receivable ?? Math.max((summary.total_receivables || 0) - (summary.total_received || 0), 0))}
              </p>
              <small style={{ color: '#7F8C8D', fontSize: '0.75rem' }}>
                {toneEnabled ? `Ab tak aaya: ${formatAmount(summary.total_received || summary.total_income || 0)}` : `Received so far: ${formatAmount(summary.total_received || summary.total_income || 0)}`}
              </small>
            </div>
            {/* Card 3: Net Balance (Net Udhar) with Overpayment Detection */}
            {(() => {
              // OVERPAYMENT DETECTION
              // If total_paid > total_payables, it means advance was paid
              // If total_received > total_receivables, it means advance was received
              const totalPaid = parseFloat(summary.total_paid || summary.total_expense || 0);
              const totalPayables = parseFloat(summary.total_payables || 0);
              const totalReceived = parseFloat(summary.total_received || summary.total_income || 0);
              const totalReceivables = parseFloat(summary.total_receivables || 0);

              const advancePaidToVendor = Math.max(totalPaid - totalPayables, 0); // We paid more than owed
              const advanceReceivedFromVendor = Math.max(totalReceived - totalReceivables, 0); // Vendor paid us more than owed

              const hasOverpayment = advancePaidToVendor > 0 || advanceReceivedFromVendor > 0;
              const netBalance = summary.net_balance || 0;

              // Determine display status
              let statusColor = '#7F8C8D';
              let statusText = toneEnabled ? 'Hisab barabar' : 'Settled';
              let statusAmount = formatAmount(0);

              if (advancePaidToVendor > 0) {
                // We paid advance to vendor - vendor owes us
                statusColor = '#27AE60';
                statusText = toneEnabled ? 'Advance diya (Adjust baad mein)' : 'Advance Paid (Adjust Later)';
                statusAmount = `+${formatAmount(advancePaidToVendor)}`;
              } else if (advanceReceivedFromVendor > 0) {
                // Vendor paid us advance - we owe vendor
                statusColor = '#F39C12';
                statusText = toneEnabled ? 'Advance mila (Adjust baad mein)' : 'Advance Received (Adjust Later)';
                statusAmount = `-${formatAmount(advanceReceivedFromVendor)}`;
              } else if (netBalance > 0) {
                statusColor = '#27AE60';
                statusText = toneEnabled ? 'Vendor se lena hai' : 'Vendor owes you';
                statusAmount = `+${formatAmount(netBalance)}`;
              } else if (netBalance < 0) {
                statusColor = '#F39C12';
                statusText = toneEnabled ? 'Vendor ko dena hai' : 'You owe vendor';
                statusAmount = `-${formatAmount(Math.abs(netBalance))}`;
              }

              return (
                <div className="summary-card" style={{ borderLeft: `4px solid ${statusColor}` }}>
                  <h3>{toneEnabled ? 'Net Udhar' : 'Net Outstanding'}</h3>
                  <p className="amount" style={{ color: statusColor }}>{statusAmount}</p>
                  <small style={{ color: '#7F8C8D', fontSize: '0.75rem' }}>{statusText}</small>
                  {hasOverpayment && (
                    <small style={{ display: 'block', color: '#856404', fontSize: '0.7rem', marginTop: 4, fontStyle: 'italic' }}>
                      {advancePaidToVendor > 0
                        ? (toneEnabled ? `₹${advancePaidToVendor.toFixed(0)} extra diya - agle order mein adjust hoga` : `₹${advancePaidToVendor.toFixed(0)} extra paid - will be adjusted in future`)
                        : (toneEnabled ? `₹${advanceReceivedFromVendor.toFixed(0)} extra mila - agle order mein adjust hoga` : `₹${advanceReceivedFromVendor.toFixed(0)} extra received - will be adjusted in future`)}
                    </small>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Date Filter & Range Display */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiFilter size={16} style={{ color: '#7F8C8D' }} />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.9rem' }}
            >
              <option value="all">{toneEnabled ? 'Sab Dikhaao' : 'All Time'}</option>
              <option value="current_month">{toneEnabled ? 'Is Mahine' : 'Current Month'}</option>
              <option value="last_month">{toneEnabled ? 'Pichle Mahine' : 'Last Month'}</option>
              <option value="last_3_months">{toneEnabled ? 'Pichle 3 Mahine' : 'Last 3 Months'}</option>
            </select>
          </div>
          {dateRange.start && dateRange.end && (
            <DateRangeDisplay startDate={dateRange.start} endDate={dateRange.end} toneEnabled={toneEnabled} />
          )}
        </div>

        {/* Transactions List */}
        <div className="card">
          <h2 style={{ marginTop: 0, marginBottom: 16 }}>
            {toneEnabled ? 'Saare Lenden' : 'All Transactions'}
            <span style={{ color: '#7F8C8D', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: 8 }}>
              ({filteredTransactions.length}{dateFilter !== 'all' ? ` / ${transactions.length}` : ''})
            </span>
          </h2>

          {filteredTransactions.length === 0 ? (
            <p style={{ color: '#7F8C8D', textAlign: 'center', padding: 20 }}>
              {toneEnabled ? 'Koi lenden nahi mila' : 'No transactions found'}
            </p>
          ) : (
            <TransactionList
              transactions={filteredTransactions}
              columns={['date', 'type', 'description', 'category', 'ledger', 'amount']}
              getTypeInfo={getTransactionTypeInfo}
              formatDate={formatDate}
              t={t}
              toneEnabled={toneEnabled}
              showActions={false}
            />
          )}
        </div>

        {/* Transaction Modal */}
        {showTxnModal && (
          <VendorTransactionModal
            isOpen={showTxnModal}
            onClose={() => setShowTxnModal(false)}
            onSuccess={handleTxnSuccess}
            vendors={[vendor]}
            preSelectedVendorId={vendor.id}
          />
        )}
      </div>
    </Layout>
  );
};

export default VendorDetail;
